import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Download, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { importNotionData, getImportPreview } from '../lib/notionImport';
import Button from '../components/Button';

export default function ImportNotionData() {
  const navigate = useNavigate();
  const [isImporting, setIsImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    goalCount: number;
    ladderCount: number;
    stepCount: number;
    error?: string;
  } | null>(null);

  const preview = getImportPreview();

  const handleImport = async () => {
    try {
      setIsImporting(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const result = await importNotionData(user.id);
      setImportResult(result);
      setImportComplete(true);

      if (result.success) {
        console.log('Import successful:', result);
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportResult({
        success: false,
        goalCount: 0,
        ladderCount: 0,
        stepCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      setImportComplete(true);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Download className="text-blue-600" size={32} />
            <h1 className="text-3xl font-bold text-slate-900">Import Notion Data</h1>
          </div>

          {!importComplete ? (
            <>
              <div className="mb-8">
                <p className="text-slate-600 mb-4">
                  This will import your goals and action ladders from Notion. Your existing goals will be preserved.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">What will be imported:</h3>
                  <ul className="space-y-1 text-blue-800">
                    <li>• {preview.totalGoals} Goals</li>
                    <li>• {preview.totalLadders} Ladders</li>
                    <li>• {preview.totalSteps} Steps</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6 mb-8">
                <h2 className="text-xl font-semibold text-slate-900">Preview:</h2>

                {preview.goals.map((goal, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-4">
                    <h3 className="font-semibold text-lg text-slate-900 mb-2">
                      {goal.name}
                    </h3>
                    <p className="text-slate-600 text-sm mb-3">
                      {goal.final_outcome}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span className="bg-slate-100 px-2 py-1 rounded">
                        {goal.ladderCount} {goal.ladderCount === 1 ? 'Ladder' : 'Ladders'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-1">Important</h4>
                    <p className="text-sm text-amber-800">
                      This is a one-time import. Make sure you want to proceed before clicking the import button.
                      This will add new goals to your existing data without removing anything.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleImport}
                disabled={isImporting}
                className="w-full py-3 text-lg"
              >
                {isImporting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Importing...
                  </span>
                ) : (
                  'Import Data'
                )}
              </Button>
            </>
          ) : (
            <div>
              {importResult?.success ? (
                <div className="text-center">
                  <CheckCircle2 className="text-green-600 mx-auto mb-4" size={64} />
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    Import Successful!
                  </h2>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-green-900 mb-3">Imported:</h3>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-3xl font-bold text-green-700">
                          {importResult.goalCount}
                        </div>
                        <div className="text-sm text-green-600">Goals</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-green-700">
                          {importResult.ladderCount}
                        </div>
                        <div className="text-sm text-green-600">Ladders</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-green-700">
                          {importResult.stepCount}
                        </div>
                        <div className="text-sm text-green-600">Steps</div>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="w-full py-3 text-lg"
                  >
                    View Your Goals
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <AlertCircle className="text-red-600 mx-auto mb-4" size={64} />
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    Import Failed
                  </h2>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                    <p className="text-red-800">
                      {importResult?.error || 'An unknown error occurred during import.'}
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      setImportComplete(false);
                      setImportResult(null);
                    }}
                    className="w-full py-3 text-lg"
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
